import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CallOutError } from "components/CallOutError/CallOutError";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { captureException } from "@sentry/react";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import axios from "axios";
import { getEnvVar } from "ideas.env";
import AppLoading from "components/AppLoading/AppLoading";
import { useQuery } from "@tanstack/react-query";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { Tool } from "components/ToolBrowser/ToolBrowser.api";
import { ToolVersion } from "graphql/_Types";
import { ToolSpec } from "types/json-schemas/type_guards";
import assert from "assert";

export const useToolSpecProviderQuery = (tenantId: number) => {
  return useQuery<Tool[], Error>({
    queryKey: ["tool_spec_provider"],
    queryFn: async () => {
      const baseUrl = getEnvVar("URL_TENANT");
      const url = `${baseUrl}tools/${tenantId}/`;
      const headers = await getRequestHeaders();
      const res = await axios.get<Tool[]>(url, { headers });
      return res.data;
    },
  });
};

type ToolSpecProviderContextValue = {
  availableToolSpecs: ToolSpecAndToolVersionId[];
  docsMap: DocsMap;
};

export const ToolSpecProviderContext = createContext<
  ToolSpecProviderContextValue | undefined
>(undefined);

export const useToolSpecContext = () => {
  const context = useContext(ToolSpecProviderContext);

  if (context === undefined) {
    throw new ContextOutOfBoundsError("UserProviderContext");
  }

  return context;
};

interface ToolSpecProviderProps {
  children: React.ReactNode;
}

export type ToolSpecAndToolVersionId = {
  spec: ToolSpec;
  toolId: Tool["id"];
  toolName: Tool["name"];
  toolVersionId: ToolVersion["id"];
  toolVersionVersion: ToolVersion["version"];
  status: ToolVersion["status"];
  credits: ToolVersion["credits"];
  versionCreated: ToolVersion["created"];
  toolCreated: Tool["created"];
  toolHelp: Tool["help_text"];
};

type DocsMap = {
  [key: Tool["key"]]:
    | {
        default: string;
        [version: ToolVersion["version"]]: string | undefined;
      }
    | undefined;
};

export const ToolSpecProvider = ({ children }: ToolSpecProviderProps) => {
  const currentTenant = useTenantContext((s) => s.currentTenant);
  const {
    data,
    error,
    isLoading: loading,
  } = useToolSpecProviderQuery(currentTenant.id);

  const [docs, setDocs] = useState<DocsMap | "loading" | "error">("loading");

  useEffect(() => {
    axios
      .get<DocsMap>(getEnvVar("URL_DOCS_MAPPER"))
      .then((res) => {
        setDocs(res.data);
      })
      .catch(() => {
        captureException("Failed to fetch tool docs");
        setDocs("error");
      });
  }, []);

  const availableToolSpecs = useMemo(() => {
    return data
      ?.filter((tool) => tool.latest_tool_version?.tool_spec)
      .map((tool) => {
        const toolVersion = tool.latest_tool_version;
        assert(toolVersion !== null, "Tool is version");
        const spec = toolVersion?.tool_spec;
        assert(spec !== null, "Tool is missing spec");
        return {
          toolId: tool.id,
          toolName: tool.name,
          toolHelp: tool.help_text,
          toolVersionId: toolVersion.id,
          toolVersionVersion: toolVersion.version,
          spec,
          status: tool.status,
          credits: toolVersion.credits,
          versionCreated: toolVersion.created,
          toolCreated: tool.created,
        };
      });
  }, [data]);

  if (loading || docs === "loading") {
    return <AppLoading />;
  }

  if (error || availableToolSpecs === undefined) {
    return <CallOutError />;
  }

  return (
    <ToolSpecProviderContext.Provider
      value={{
        availableToolSpecs,
        // Don't want to fail to load the entire app if we fail to fetch docs links
        docsMap: docs !== "error" ? docs : ({} as DocsMap),
      }}
    >
      {children}
    </ToolSpecProviderContext.Provider>
  );
};
